import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FadeUp from '../components/FadeUp';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams({ 'form-name': 'contact-form', ...form }).toString();
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    }
    setSubmitting(false);
  };

  const styles = {
    page: { paddingTop: '68px' },
    hero: {
      background: '#C0392B',
      padding: '100px 24px 64px',
      textAlign: 'center',
    },
    heroHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(36px, 5vw, 54px)',
      color: '#FFFFFF',
      marginBottom: '12px',
    },
    heroSub: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '18px',
      color: '#E67E22',
      fontWeight: 600,
    },
    contactSection: {
      background: '#FFFFFF',
      padding: '88px 24px',
    },
    contactInner: {
      maxWidth: '1100px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '64px',
      alignItems: 'start',
    },
    formCard: {
      background: '#FFFFFF',
      borderRadius: '20px',
      border: '1px solid rgba(0,0,0,0.08)',
      padding: '40px',
      boxShadow: '0 4px 32px rgba(0,0,0,0.06)',
    },
    formHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: '26px',
      color: '#1A1A1A',
      marginBottom: '8px',
    },
    formSub: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '15px',
      color: '#666',
      marginBottom: '28px',
    },
    fieldGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '14px',
      color: '#333',
      marginBottom: '6px',
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1.5px solid #e0d6cc',
      borderRadius: '10px',
      fontFamily: 'Inter, sans-serif',
      fontSize: '15px',
      color: '#1A1A1A',
      background: '#FAFAFA',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '12px 16px',
      border: '1.5px solid #e0d6cc',
      borderRadius: '10px',
      fontFamily: 'Inter, sans-serif',
      fontSize: '15px',
      color: '#1A1A1A',
      background: '#FAFAFA',
      outline: 'none',
      resize: 'vertical',
      minHeight: '120px',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box',
    },
    submitBtn: {
      width: '100%',
      padding: '15px',
      background: '#C0392B',
      color: '#FFFFFF',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: '16px',
      borderRadius: '10px',
      border: 'none',
      cursor: 'pointer',
      transition: 'background 0.2s, transform 0.2s',
      marginTop: '8px',
    },
    errorMsg: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      color: '#C0392B',
      marginTop: '12px',
      textAlign: 'center',
    },
    successCard: {
      textAlign: 'center',
      padding: '60px 32px',
    },
    successIcon: {
      fontSize: '56px',
      marginBottom: '16px',
    },
    successHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: '24px',
      color: '#27AE60',
      marginBottom: '10px',
    },
    successText: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      color: '#555',
    },
    infoSide: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    infoHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: '26px',
      color: '#1A1A1A',
      marginBottom: '4px',
    },
    infoSub: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '15px',
      color: '#666',
      marginBottom: '16px',
    },
    infoItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
    },
    infoIcon: {
      width: '44px',
      height: '44px',
      background: '#FDF6EC',
      border: '1.5px solid #f0e0cc',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      flexShrink: 0,
    },
    infoLabel: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '13px',
      color: '#999',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      marginBottom: '2px',
    },
    infoValue: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '15px',
      color: '#1A1A1A',
      fontWeight: 500,
    },
    mapSection: {
      background: '#FDF6EC',
      padding: '0 24px 80px',
    },
    mapInner: {
      maxWidth: '1100px',
      margin: '0 auto',
    },
    mapHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 700,
      fontSize: '22px',
      color: '#1A1A1A',
      marginBottom: '20px',
      paddingTop: '64px',
    },
    mapWrap: {
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    },
  };

  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={styles.hero} aria-label="Contact page hero">
        <FadeUp>
          <h1 style={styles.heroHeading}>Contact Us</h1>
          <p style={styles.heroSub}>We're here and happy to help</p>
        </FadeUp>
      </section>

      {/* Form and Info */}
      <section style={styles.contactSection} aria-label="Contact form and details">
        <div style={styles.contactInner} className="contact-grid">
          {/* Form */}
          <FadeUp>
            <div style={styles.formCard}>
              <AnimatePresence mode="wait">
                {!submitted ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h2 style={styles.formHeading}>Send Us a Message</h2>
                    <p style={styles.formSub}>Fill in the form below and we will get back to you soon.</p>

                    <form
                      name="contact-form"
                      method="POST"
                      data-netlify="true"
                      onSubmit={handleSubmit}
                    >
                      <input type="hidden" name="form-name" value="contact-form" />

                      <div style={styles.fieldGroup}>
                        <label htmlFor="name" style={styles.label}>Your Name</label>
                        <input
                          id="name"
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="e.g. Jane Smith"
                          style={styles.input}
                          onFocus={(e) => { e.target.style.borderColor = '#C0392B'; e.target.style.boxShadow = '0 0 0 3px rgba(192,57,43,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#e0d6cc'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <label htmlFor="phone" style={styles.label}>Phone Number</label>
                        <input
                          id="phone"
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="e.g. 06-348 0000"
                          style={styles.input}
                          onFocus={(e) => { e.target.style.borderColor = '#C0392B'; e.target.style.boxShadow = '0 0 0 3px rgba(192,57,43,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#e0d6cc'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <label htmlFor="email" style={styles.label}>Email Address</label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="e.g. jane@example.com"
                          style={styles.input}
                          onFocus={(e) => { e.target.style.borderColor = '#C0392B'; e.target.style.boxShadow = '0 0 0 3px rgba(192,57,43,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#e0d6cc'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <label htmlFor="message" style={styles.label}>Message</label>
                        <textarea
                          id="message"
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          required
                          placeholder="How can we help you?"
                          style={styles.textarea}
                          onFocus={(e) => { e.target.style.borderColor = '#C0392B'; e.target.style.boxShadow = '0 0 0 3px rgba(192,57,43,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#e0d6cc'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1 }}
                        onMouseEnter={(e) => { if (!submitting) { e.currentTarget.style.background = '#a93226'; e.currentTarget.style.transform = 'scale(1.01)'; }}}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#C0392B'; e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        {submitting ? 'Sending...' : 'Send Message'}
                      </button>

                      {error && <p style={styles.errorMsg}>{error}</p>}
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    style={styles.successCard}
                  >
                    <div style={styles.successIcon} role="img" aria-label="Success">&#10003;</div>
                    <h3 style={styles.successHeading}>Thanks, we will be in touch soon!</h3>
                    <p style={styles.successText}>
                      We appreciate you getting in touch. Our team will respond to your message shortly.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeUp>

          {/* Info */}
          <FadeUp delay={0.15}>
            <div style={styles.infoSide}>
              <div>
                <h2 style={styles.infoHeading}>Get in Touch</h2>
                <p style={styles.infoSub}>We are always happy to hear from you. Call us, email us or drop by.</p>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoIcon} aria-hidden="true">&#128205;</div>
                <div>
                  <p style={styles.infoLabel}>Location</p>
                  <p style={styles.infoValue}>Whanganui, New Zealand</p>
                </div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoIcon} aria-hidden="true">&#128222;</div>
                <div>
                  <p style={styles.infoLabel}>Phone</p>
                  <a href="tel:0634844398" style={{ ...styles.infoValue, textDecoration: 'none', color: '#C0392B' }}>
                    06-348 4398
                  </a>
                </div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.infoIcon} aria-hidden="true">&#9993;</div>
                <div>
                  <p style={styles.infoLabel}>Email</p>
                  <a
                    href="mailto:shiriwastowenterprises@gmail.com"
                    style={{ ...styles.infoValue, textDecoration: 'none', color: '#C0392B', wordBreak: 'break-all', fontSize: '14px' }}
                  >
                    shiriwastowenterprises@gmail.com
                  </a>
                </div>
              </div>

              <div style={{
                background: '#FDF6EC',
                border: '1px solid #f0e0cc',
                borderRadius: '14px',
                padding: '20px 22px',
                marginTop: '8px',
              }}>
                <p style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px', color: '#1A1A1A', marginBottom: '6px' }}>
                  Trading As
                </p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#666' }}>
                  Shiriwastow Enterprises Ltd
                </p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#999', marginTop: '4px' }}>
                  Whanganui Spices and Veges
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Map */}
      <section style={styles.mapSection} aria-label="Store location map">
        <div style={styles.mapInner}>
          <FadeUp>
            <h2 style={styles.mapHeading}>Find Us in Whanganui</h2>
            <div style={styles.mapWrap}>
              <iframe
                src="https://maps.google.com/maps?q=Whanganui+Spices+and+Veges+Whanganui+New+Zealand&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="400"
                style={{ border: 0, display: 'block' }}
                allowFullScreen=""
                loading="lazy"
                title="Whanganui Spices and Veges location map"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
